/**
 * PlaybackIntercept middleware
 *
 * Intercepts actions that play music if needed.
 *
 * @module middleware/PlaybackIntercept
 */

import * as D from './deps';

// our local logger instance
const log = D.Logger.child({
  component: 'PlaybackIntercept',
});

function isStreamViolationTrigger(action) {
  if (action.type === D.Constants.UseCases.ActionTypes.ADD_TRACK_FEEDBACK) {
    return action.payload.skip;
  }

  const actions = [
    D.Constants.UseCases.ActionTypes.START_STATION,
    D.Constants.Playback.ActionTypes.TIRE_OF_TRACK,
    D.Constants.UseCases.ActionTypes.SKIP_TRACK_FORWARD,
    D.Constants.UseCases.ActionTypes.REPLAY_TRACK,
    D.Constants.Playback.ActionTypes.REQUEST_PLAY_TRACK,
    D.Constants.Playback.ActionTypes.INTENT_PLAY_TRACK,
  ];
  return actions.includes(action.type);
}

function shouldShowStreamViolation(action, getState) {
  try {
    return (
      D.getInDomain('playback.utils.isStreamViolationValidAndNotStale')(getState()) &&
      isStreamViolationTrigger(action)
    );
  } catch (_error) {
    log.trace('Sim stream domain not loaded, utils not registered');
    return false;
  }
}

export const interceptPlayback =
  () =>
  (action, { dispatch, getState }) => {
    return new Promise((resolve, reject) => {
      if (!action.payload) {
        // TODO-PAYLOAD:: WEBCA-2505
        action.payload = action.data;
      }

      if (action.payload?.dontIntercept) {
        resolve();
      } else if (D.Utils.Listener.isAnonymousListener(getState())) {
        // redirect to playback URL after auth
        if (action.payload?.id) {
          const pandoraId = action.payload.id;
          const sourceType = D.Utils.SourceType.getSourceTypeFromPandoraId(pandoraId);
          const redirectUrl = D.Utils.Route.getNowPlayingPathForId(
            getState(),
            pandoraId,
            sourceType,
          );
          dispatch(D.Actions.Login.setAfterLoginRedirect(redirectUrl));
        }

        if (D.Utils.Listener.isAnonTimedOut(getState())) {
          // if this is a track end,play a message
          if (action.type === D.Constants.AudioPlayer.AudioPlayerActions.ENDED) {
            // TODO: WEB-8882 currently uses the sample playback mechanism. should be refactored in the future
            dispatch(
              D.Actions.SamplePlayer.playSample(D.Constants.App.ANON_TRIAL_TIMEOUT_MESSAGE, false),
            );
          }

          // if timedout, throw up a modal and noop the action.
          log.debug('changing action.type to noop', JSON.stringify(action));
          action.type = D.Constants.Playback.ActionTypes.NOOP;
          dispatch(D.Actions.Modal.openSignupLoginModal());
        }
        resolve();
      } else if (shouldShowStreamViolation(action, getState)) {
        // Delegate the action to fire only if user chooses to keep listening
        dispatch(
          D.getInDomain('playback.actions.promptStreamViolation')({
            triggerAction: action,
          }),
        );
        reject();
      } else if (D.getInDomain('application.utils.isAntiAdBlockerHammerModeDetected')(getState())) {
        dispatch(D.Actions.Application.openAdBlockerModal());
        reject();
      } else {
        resolve();
      }
    });
  };

export const route = () => {
  return D.createActionMapping({
    [D.Constants.UseCases.ActionTypes.START_STATION]: interceptPlayback(),
    [D.Constants.UseCases.ActionTypes.ADD_TRACK_FEEDBACK]: interceptPlayback(),
    [D.Constants.UseCases.ActionTypes.REMOVE_TRACK_FEEDBACK]: interceptPlayback(),
    [D.Constants.AudioPlayer.AudioPlayerActions.MUTE]: interceptPlayback(),
    [D.Constants.AudioPlayer.AudioPlayerActions.UNMUTE]: interceptPlayback(),
    [D.Constants.AudioPlayer.AudioPlayerActions.SET_VOLUME]: interceptPlayback(),
    [D.Constants.AudioPlayer.AudioPlayerActions.ENDED]: interceptPlayback(),
    [D.Constants.Playback.ActionTypes.TIRE_OF_TRACK]: interceptPlayback(),
    [D.Constants.UseCases.ActionTypes.SKIP_TRACK_FORWARD]: interceptPlayback(),
    [D.Constants.UseCases.ActionTypes.REPLAY_TRACK]: interceptPlayback(),
    [D.Constants.Playback.ActionTypes.REQUEST_PLAY_TRACK]: interceptPlayback(),
    [D.Constants.Playback.ActionTypes.INTENT_PLAY_TRACK]: interceptPlayback(),
    [D.Constants.UseCases.ActionTypes.PLAY_RESUME_SOURCE]: interceptPlayback(),
  });
};

export default router => store => next => action => {
  return route(router)(action, store, next);
};
